import { DATATABLES_FIXED_CELL_SELECTOR } from 'ui/datatables/datatables';

import { OlapPivotTablePlugin } from 'cadenza/result-table/olap-pivot-table-plugin';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { DATATABLE_DESTROYED, DATATABLE_INITIALIZED } from 'cadenza/ui/base-table/base-table-event-types';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import { px } from 'cadenza/utils/px';

import './col-span-olap-pivot-table-plugin.css';

const COMPONENT_NAME = 'd-col-span-olap-pivot-table-plugin';

/**
 * Keeps content of table header cells with colspan of all rows except bottom row centered and
 * visible, by emulating CSS sticky.
 * We can't use CSS sticky for this because CSS sticky only works against the sticky element's
 * offset parent and thus would not respect cells of sticky columns (e.g. pivot table).
 */
export class ColSpanOlapPivotTablePlugin extends OlapPivotTablePlugin {

  /** Events registered per datatable lifetime */
  #resultTableEventKeys: EventKey[] = [];

  /**
   * Header cells of all but last row that are currently visible in the table's scroll body (visible
   * area of table)
   */
  #stickyCells = new Set<HTMLTableCellElement>();

  /** Detects visibility of table header cells. Detects cells hidden behind sticky cells as visible. */
  #observer?: IntersectionObserver;

  override configureWithConfigurationData (tableConfiguration: TableConfigurationDto): void {
    this.#unregisterResultTableEventsAndStopObserving();

    if (tableConfiguration.pivotConfiguration) {
      this.#resultTableEventKeys.push(
        on(this.table, DATATABLE_INITIALIZED, () => this.#onDatatableInitialized()),
        on(this.table, DATATABLE_DESTROYED, () => this.#onDatatableDestroyed()));
    }
  }

  disconnectedCallback () {
    this.#unregisterResultTableEventsAndStopObserving();
  }

  #unregisterResultTableEventsAndStopObserving () {
    unByKey(this.#resultTableEventKeys);
    this.#resultTableEventKeys.length = 0;
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #onDatatableInitialized () {
    if (this.table.getScrollBody()) { // Plugin is only necessary (and working) if we have a scrolling area
      this.table.getDataTable()?.one('init.dt.dtfc', () => this.#initVerticallyStickyCellLabels());
    }
  }

  #onDatatableDestroyed () {
    // We lost the scroll body, whose cells we observed, so we can clear our listeners as well
    this.#unregisterResultTableEventsAndStopObserving();
  }

  #initVerticallyStickyCellLabels () {
    this.#resultTableEventKeys.push(
      on(this.table.getScrollBody()!, 'scroll', () => {
        const visibleArea = this.#calculateNonFixedVisibleTableArea();
        this.#stickyCells.forEach(cell => {
          this.#updateCellLabelPosition(cell, visibleArea);
        });
      })
    );

    // Create observer for observing visibility of cells. This observation does not take into account that cells
    // scrolled under horizontally sticky cells from frozen columns are not visible: They count as visible.
    // The visibility of cells hidden by sticky cells is taken into account at first when calculating the individual
    // cell's label's new position. This could lead to more cell labels updated than necessary, but the performance
    // draw is negligible.
    this.#observer = new IntersectionObserver(entries => {
      const visibleArea = this.#calculateNonFixedVisibleTableArea();
      entries.forEach(({ target, isIntersecting }) => {
        const cell = target as HTMLTableCellElement;
        if (isIntersecting) { // Found a cell that turned visible
          this.#stickyCells.add(cell);
          this.#updateCellLabelPosition(cell, visibleArea);
        } else { // Found a cell that turned invisible
          this.#stickyCells.delete(cell);
        }
      });
    });

    // Observe all cells but those from last row (they should follow the alignment of the body cells derived from the
    // column's attribute type).
    const rows = [ ...this.table.getScrollHead()!.querySelectorAll<HTMLTableRowElement>('tr') ];
    rows.slice(0, rows.length - 1) // All but last row, which should keep the columns alignment
      .flatMap(row => [ ...row.querySelectorAll<HTMLTableCellElement>(`th[colspan]:not(${DATATABLES_FIXED_CELL_SELECTOR})`) ])
      .forEach(cell => this.#observer!.observe(cell));
  }

  #updateCellLabelPosition (cell: HTMLTableCellElement, visibleArea: HorizontalRange) {
    const label = cell.querySelector<HTMLElement>('.d-table-header--title-container')!;
    this.#updateStickyElementPosition(cell, label, visibleArea);
  }

  /**
   * Find the area on the X axis, where the labels can be displayed and are not hidden by anything,
   * including horizontally sticky columns.
   */
  #calculateNonFixedVisibleTableArea (): HorizontalRange {
    const firstCellOfMostRightFixedColumn = [ ...this.table.querySelectorAll<HTMLElement>(DATATABLES_FIXED_CELL_SELECTOR) ]
      .find(fixedCell => !fixedCell.nextElementSibling?.matches(DATATABLES_FIXED_CELL_SELECTOR))!;

    const scrollBodyBoundingRect = this.table.getScrollBody()!.getBoundingClientRect();

    const left = firstCellOfMostRightFixedColumn
      ? firstCellOfMostRightFixedColumn.getBoundingClientRect().right
      : scrollBodyBoundingRect.left;
    const right = scrollBodyBoundingRect.right;
    return { left, right };
  }

  /**
   * Updates the position (style 'left' property) of the given stickyElement so that it remains
   * horizontally visible in the given area, but still
   * @param stickyElementContainer
   * @param stickyElement
   * @param visibleArea
   * @private
   */
  #updateStickyElementPosition (stickyElementContainer: HTMLTableCellElement, stickyElement: HTMLElement, visibleArea: HorizontalRange) {
    const cellRange = getHorizontalRange(stickyElementContainer);
    const cellWidth = rangeWidth(cellRange);

    const visibleCellRange = getIntersection(cellRange, visibleArea);
    const visibleCellWidth = rangeWidth(visibleCellRange);

    if (!visibleCellRange) {
      return; // stickyElementContainer is not in visible area
    }

    // In CADENZA the stickyElementContainer often has a padding. So when deriving a left value
    // from a cells BoundingClientRect, we need to respect the padding as an additional offset on
    // the inside of the rect.
    const computedStyle = getComputedStyle(stickyElementContainer);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);

    const stickyElementWidth = elementWidth(stickyElement);

    // Center it
    let left = (cellWidth - stickyElementWidth) / 2 - paddingLeft;

    // Move to left to not cross right visible border of stickyElementContainer
    left = Math.min(left, visibleCellWidth - stickyElementWidth - paddingLeft - paddingRight);

    // Move to right to not cross right visible border of stickyElementContainer
    left = Math.max(left, visibleCellRange.left - cellRange.left);

    // Move to left to not cross right border of stickyElementContainer, ignoring visibility
    left = Math.min(left, cellWidth - stickyElementWidth - paddingLeft - paddingRight);

    stickyElement.style.transform = `translate(${px(left)}`;
    stickyElementContainer.classList.add(`${COMPONENT_NAME}--sticky-element-container`);
  }

}

customElements.define(COMPONENT_NAME, ColSpanOlapPivotTablePlugin);

interface HorizontalRange {
  left: number;
  right: number;
}

function getHorizontalRange (cell: HTMLTableCellElement): HorizontalRange {
  const boundingClientRect = cell.getBoundingClientRect();
  return {
    left: boundingClientRect.left,
    right: boundingClientRect.right
  };
}

function getIntersection (a: HorizontalRange, b: HorizontalRange): HorizontalRange | undefined {
  const maxLeft = Math.max(a.left, b.left);
  const minRight = Math.min(a.right, b.right);

  // Do ranges intersect?
  if (maxLeft <= minRight) {
    return { left: maxLeft, right: minRight };
  }
}

function rangeWidth ({ left, right }: HorizontalRange = { left: 0, right: 0 }) {
  return right - left;
}

function elementWidth (element: HTMLElement) {
  return element.getBoundingClientRect().width;
}
